/**
 * useSidebarState Hook
 * Manages sidebar collapse state and admin section expand/collapse state.
 * Persists both to localStorage so they survive page reloads.
 */
import { useState, useCallback, useEffect } from 'react';

const STORAGE_KEY_COLLAPSED = 'sidebarCollapsed';
const STORAGE_KEY_SECTIONS = 'sidebarSections';

/**
 * Admin section group definitions.
 * Items are matched by their `text` property in the sidebar menu arrays.
 */
export const ADMIN_GROUPS = [
  {
    key: 'users_access',
    label: 'Users & Access',
    items: ['Service Status', 'Pending Users', 'User Management', 'Role Management', 'Tenant Management'],
  },
  {
    key: 'configuration',
    label: 'Configuration',
    items: ['Reference Data', 'Calculation Profiles', 'Document Types', 'Workflow Doc Requirements', 'API Settings', 'Tenant Settings'],
  },
  {
    key: 'communications',
    label: 'Communications',
    items: ['Email Settings', 'Email Recipients', 'Email Audit', 'PDF Templates'],
  },
];

export default function useSidebarState() {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY_COLLAPSED) === 'true';
    } catch {
      return false;
    }
  });

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY_COLLAPSED, String(next)); } catch {}
      return next;
    });
  }, []);

  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY_SECTIONS);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY_SECTIONS, JSON.stringify(expandedSections));
    } catch {}
  }, [expandedSections]);

  const toggleSection = useCallback((sectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  const isSectionExpanded = useCallback(
    (sectionKey) => !!expandedSections[sectionKey],
    [expandedSections]
  );

  /**
   * Auto-expand the group whose items contain the currently active route.
   * Call this on mount and route change to ensure the right group is open.
   */
  const autoExpandForPath = useCallback((currentPath, adminItems) => {
    for (const group of ADMIN_GROUPS) {
      const match = adminItems.find(
        (item) => group.items.includes(item.text) && item.path === currentPath
      );
      if (match) {
        setExpandedSections((prev) => {
          if (prev[group.key]) return prev; // already expanded, no change
          return { ...prev, [group.key]: true };
        });
        break;
      }
    }
  }, []);

  return {
    collapsed,
    toggleCollapsed,
    toggleSection,
    isSectionExpanded,
    autoExpandForPath,
  };
}
